import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Checkbox } from '@mui/material'
import { TariffUpgradeType } from '../../methods/TariffUpgradeType'
import { httpClient } from '../../funcs'
import { notifyAboutError } from '../../methods/notifyUser'
import Popup from '../../components/Popup'
import InfoButton from '../../components/InfoButton'
import { ContractLinkSmsInfoPopup } from '../../components/ContractLinkSmsInfoPopup'
import RequestBillPopupV2 from '../Profile/RequestBillPopupV2'
import './TariffUpgradePopup.sass'
import {ReactComponent as CheckboxIcon} from "../Icons/size24/Checkbox.svg";
import {ReactComponent as CheckboxChecked} from "../Icons/size24/CheckboxChecked.svg";

interface TariffUpgradePopupProps {
    isOpen: boolean
    onClose: () => void
    currentUpgrades: string[]
    onUpgradeSuccess?: () => void
    $store: any
    $commitToStore: (data: any) => void
}

let TariffUpgradePopup: any = function TariffUpgradePopup({
    isOpen,
    onClose,
    currentUpgrades,
    onUpgradeSuccess,
    $store,
    $commitToStore
}: TariffUpgradePopupProps) {
    const [selectedUpgrades, setSelectedUpgrades] = useState<TariffUpgradeType[]>([])
    const [upgradePrices, setUpgradePrices] = useState<{[key: string]: any}>({})
    const [totalPrice, setTotalPrice] = useState(0)
    const [documentsInfo, setDocumentsInfo] = useState<any>({})
    const [loading, setLoading] = useState(false)
    const [calculating, setCalculating] = useState(false)
    const [showBillPopup, setShowBillPopup] = useState(false)
    const [showContractLinkSmsInfo, setShowContractLinkSmsInfo] = useState(false)

    // Загружаем цены всех апгрейдов при открытии попапа
    useEffect(() => {
        if (isOpen) {
            loadUpgradePrices(
                TariffUpgradeType.availableUpgradesForJurisdiction(
                    $store?.userSession?.jurisdiction,
                ),
            )
        }
    }, [isOpen, $store?.userSession?.jurisdiction])

    // Пересчитываем общую сумму при изменении выбранных апгрейдов
    useEffect(() => {
        let total = 0
        selectedUpgrades.forEach(upgrade => {
            const upgradeInfo = upgradePrices[upgrade]
            if (upgradeInfo && upgradeInfo.price) {
                total += upgradeInfo.price
            }
        })
        setTotalPrice(total)
    }, [selectedUpgrades, upgradePrices])

    const loadUpgradePrices = async (upgrades: TariffUpgradeType[]) => {
        if (upgrades.length === 0) return

        setCalculating(true)
        try {
            const { data } = await httpClient.post('/user/calculate-upgrade-price-v2', {
                tariff_upgrades: upgrades
            })
            setUpgradePrices(data.upgrades || {})
            setDocumentsInfo(data.documents_info || {})
        } catch (error) {
            console.error('Failed to load upgrade prices:', error)
            notifyAboutError({ $store, $commitToStore }, error)
        } finally {
            setCalculating(false)
        }
    }

    const handleUpgradeToggle = (upgrade: TariffUpgradeType) => {
        setSelectedUpgrades(prev => {
            if (prev.includes(upgrade)) {
                return prev.filter(u => u !== upgrade)
            }
            return [...prev, upgrade]
        })
    }

    const handlePayment = async (method: 'card' | 'invoice') => {
        if (selectedUpgrades.length === 0) return

        if (method === 'card') {
            setLoading(true)
            try {
                // Создаем корзину с апгрейдами
                const cartItems = selectedUpgrades.map(upgrade => ({
                    _id: upgrade,
                    name: TariffUpgradeType.getTitle(upgrade),
                    type: 'upgrade',
                    price: upgradePrices[upgrade]?.price || 0,
                    quantity: 1
                }))

                const { data: result } = await httpClient.post('/buy-cart', {
                    cart: cartItems
                })

                if (result.PaymentURL) {
                    window.location.href = result.PaymentURL
                } else {
                    notifyAboutError({ $store, $commitToStore }, new Error('Не удалось создать платеж'))
                }
            } catch (error) {
                notifyAboutError({ $store, $commitToStore }, error)
            } finally {
                setLoading(false)
            }
        } else if (method === 'invoice') {
            // Открываем попап заказа счета
            setShowBillPopup(true)
        }
    }

    const handleBillCreated = () => {
        // Закрываем попап заказа счета
        setShowBillPopup(false)
        // Закрываем основной попап апгрейдов
        onClose()
        // Вызываем callback успешного обновления
        if (onUpgradeSuccess) {
            onUpgradeSuccess()
        }
    }

    const handlePaymentProofUploaded = () => {
        // Закрываем попап заказа счета после загрузки платежного поручения
        setShowBillPopup(false)
        // Закрываем основной попап апгрейдов
        onClose()
        // Вызываем callback успешного обновления
        if (onUpgradeSuccess) {
            onUpgradeSuccess()
        }
    }

    const renderUpgradeItem = (upgrade: TariffUpgradeType) => {
        const isPurchased = currentUpgrades.includes(upgrade)
        const isSelected = selectedUpgrades.includes(upgrade)
        const upgradeInfo = upgradePrices[upgrade] || {}
        const price = upgradeInfo.price || 0
        const pricePerDocument = upgradeInfo.price_per_document || 0
        const title = TariffUpgradeType.getTitle(upgrade)

        return (
            <div key={upgrade} className="upgrade-item">
                <div className="upgrade-info">
                    <Checkbox
                        icon={<CheckboxIcon className="icon"/>}
                        checkedIcon={<CheckboxChecked className="icon"/>}
                        checked={isPurchased || isSelected}
                        disabled={isPurchased}
                        onChange={() => !isPurchased && handleUpgradeToggle(upgrade)}
                    />
                    <div className="upgrade-details">
                        <div className="upgrade-title">
                            {title}
                            {isPurchased && <span style={{ marginLeft: '8px', color: '#4CAF50' }}>✓</span>}
                        </div>
                        <div className="upgrade-description" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {isPurchased
                                ? 'Уже подключено'
                                : upgrade === TariffUpgradeType.ContractLinkSms
                                    ? 'SMS со ссылкой при выставлении документа'
                                    : 'Дополнительная услуга'}
                            {upgrade === TariffUpgradeType.ContractLinkSms && (
                                <InfoButton
                                    size="small"
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        setShowContractLinkSmsInfo(true)
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </div>
                <div className="upgrade-price">
                    {isPurchased ? (
                        <div style={{ color: '#4CAF50', fontSize: '14px' }}>
                            Подключено
                        </div>
                    ) : (
                        <div className="price-details">
                            <div className="price-main">
                                {price > 0 ? `+${price / 100} ₽` : '0 ₽'}
                            </div>
                            {pricePerDocument > 0 && (
                                <div className="price-per-document">
                                    {pricePerDocument / 100} ₽ за документ
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    if (!isOpen) return null

    return (
        <>
        <Popup onClose={onClose}>
            <div style={{ padding: '24px', minWidth: '400px' }}>
                <h3 style={{ marginBottom: '24px', fontSize: '20px', fontWeight: '600' }}>
                    Улучшить тариф
                </h3>

                {/* Информация о документах */}
                {documentsInfo.total_documents > 0 && (
                    <div style={{ 
                        marginBottom: '20px', 
                        padding: '12px', 
                        backgroundColor: '#f5f5f5', 
                        borderRadius: '8px',
                        fontSize: '14px',
                        color: '#666'
                    }}>
                        <div style={{ marginBottom: '4px' }}>
                            <strong>Активных пакетов:</strong> {documentsInfo.packages_count}
                        </div>
                        <div style={{ marginBottom: '4px' }}>
                            <strong>Всего документов:</strong> {documentsInfo.total_documents}
                        </div>
                        <div>
                            <strong>Осталось документов:</strong> {documentsInfo.remaining_documents}
                        </div>
                    </div>
                )}

                <div className="upgrade-popup">
                    <div className="upgrade-list">
                        <div className="upgrade-section-title">Проверки подписантов</div>
                        {TariffUpgradeType.signerVerificationUpgrades.map(renderUpgradeItem)}

                        <div className="upgrade-section-title" style={{ marginTop: '16px' }}>
                            Код подтверждения при подписании
                        </div>
                        {TariffUpgradeType.signingCommunicationUpgradesForJurisdiction(
                            $store?.userSession?.jurisdiction,
                        ).map(renderUpgradeItem)}

                        <div className="upgrade-section-title" style={{ marginTop: '16px' }}>
                            Ссылка клиенту
                        </div>
                        {TariffUpgradeType.contractLinkUpgrades.map(renderUpgradeItem)}
                    </div>

                    {totalPrice > 0 && (
                        <div className="upgrade-summary">
                            <div className="total-price">
                                <div className="total-label">Итого к доплате:</div>
                                <div className="total-amount">
                                    {calculating ? '...' : `${totalPrice / 100} ₽`}
                                </div>
                            </div>

                            <div className="payment-actions">
                                <button
                                    className="action-button secondary"
                                    onClick={() => handlePayment('invoice')}
                                    disabled={loading}
                                >
                                    <span className="button-text">Заказать счет</span>
                                </button>
                                <button
                                    className="action-button primary"
                                    onClick={() => handlePayment('card')}
                                    disabled={loading || calculating}
                                >
                                    <span className="button-text">
                                        {loading ? 'Обработка...' : 'Оплатить картой'}
                                    </span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Popup>

        {/* Попап заказа счета */}
        {showContractLinkSmsInfo && (
            <ContractLinkSmsInfoPopup onClose={() => setShowContractLinkSmsInfo(false)} />
        )}

        {showBillPopup && (
            <RequestBillPopupV2
                close={() => setShowBillPopup(false)}
                onCollectBonus={() => {}}
                onBillCreated={handleBillCreated}
                onLoadTariffs={() => {}}
                onPaymentProofUploaded={handlePaymentProofUploaded}
                $store={$store}
                $commitToStore={$commitToStore}
                items={selectedUpgrades.map(upgrade => ({
                    _id: upgrade,
                    name: TariffUpgradeType.getTitle(upgrade),
                    type: 'upgrade',
                    price: upgradePrices[upgrade]?.price || 0,
                    quantity: 1
                }))}
            />
        )}
    </>
    )
}

TariffUpgradePopup = connect(
    (store) => ({ $store: store }),
    (dispatch) => ({
        $commitToStore: (data: any) => dispatch({ ...data, type: 'S' })
    })
)(TariffUpgradePopup)

export default TariffUpgradePopup
